import React from 'react';
import type { FC } from 'react';
import { Flex } from '@chakra-ui/react';
import parse from 'html-react-parser';
import { FormattedMessage } from 'react-intl';
import { ConditionalRender } from '../../../../components/conditionally-render/ConditionalRender';
import { ResponsiveImage } from '../../../../components/responsive-image';
import { LimitedQuantityLabel, OnlyXLeftLabel } from '../../../../components/scarcity-labels';
import { Text } from '../../../../ui-components';
import { isExtendPostPurchase } from '../../../../utils/product-utils';
import { useCartState } from '../../contexts/useCartState';
import { CartDeliveryOptions } from '../cart-delivery-options';
import { CartItemPrice } from '../cart-item-price/CartItemPrice';
import { CartQuantityBox } from '../cart-quantity-box/CartQuantityBox';
import { DeleteProductItemButton } from './DeleteProductItemButton';
import { ProductLink } from './ProductLink';
import type { ProductItemEditor } from '../../../../bff-api/baskets/model';
import type { Product } from '../../../../bff-api/products/model';

export interface BaseItemProps {
    productItem: ProductItemEditor;
    product?: Product;
    id: string;
    isLimitedQuantityBadge?: boolean;
    isOnlyXLeftBadge?: boolean;
    isSaleFinal?: boolean;
    availableToSell?: number;
    middleSection?: React.ReactNode;
    footerSection?: React.ReactNode;
    buttonSection?: React.ReactNode;
    quantitySection?: React.ReactNode;
    isSoftLink?: boolean;
    disableLink?: boolean;
}

export const BaseItem: FC<BaseItemProps> = ({
    id,
    productItem,
    product,
    isLimitedQuantityBadge,
    isOnlyXLeftBadge,
    isSaleFinal,
    availableToSell,
    middleSection,
    footerSection,
    buttonSection,
    quantitySection,
    isSoftLink,
    disableLink,
}) => {
    const quantityComponent = quantitySection ?? <CartQuantityBox productItem={productItem} />;
    const { refManager } = useCartState();
    const {
        productId,
        c_shoppingBagImageUrl: productImage,
        productName,
        c_pwaUrl,
        itemText,
    } = productItem;
    const productTitle = isExtendPostPurchase(productItem) && itemText ? itemText : productName;

    return (
        <Flex
            ref={refManager(productId)}
            wrap="wrap"
            gap={{ base: '1.5rem 1rem', sm: '1.5rem 2rem' }}
            alignItems="flex-start"
            padding={{ base: '2rem 0' }}
            borderBottom="1px solid #C6C6C6"
            _first={{ paddingTop: 0 }}
            _last={{ paddingBottom: 0, borderBottom: 'none' }}
            data-testid={id ?? 'cart-item'}
        >
            <Flex
                gap={{ base: '1.5rem 1rem', sm: '1.5rem 2rem' }}
                width={{ base: '100%', sm: 'auto', 'lg-sm': '100%' }}
            >
                <Flex width="120px" height="fit-content" shrink={0}>
                    <ProductLink
                        c_pwaUrl={c_pwaUrl}
                        isSoftLink={isSoftLink}
                        disableLink={disableLink}
                    >
                        <ResponsiveImage
                            id={`cart-item-${productId}-image`}
                            src={productImage ?? ''}
                            alt={productTitle}
                            width="120px"
                            height="120px"
                        />
                    </ProductLink>
                </Flex>

                <Flex
                    shrink={0}
                    grow={1}
                    basis={0}
                    gap={{ base: 6, sm: 8 }}
                    direction={{ base: 'column', 'lg-sm': 'row' }}
                    justifyContent="space-between"
                    width={{
                        md: '170px',
                        'lg-sm': 'unset',
                    }}
                >
                    <Flex
                        direction={{ base: 'column', 'lg-sm': 'row' }}
                        width="100%"
                        gap={{ base: 6, sm: 8 }}
                    >
                        <Flex
                            data-testid={`cart-product-information-${productId}`}
                            gap={1}
                            wrap="wrap"
                            direction="column"
                            width={{
                                sm: '260px',
                                md: '170px',
                                'lg-sm': '50%',
                            }}
                        >
                            <Flex wrap="wrap" direction="column" gap={1}>
                                <Text style="bold">
                                    <ProductLink
                                        c_pwaUrl={c_pwaUrl}
                                        isSoftLink={isSoftLink}
                                        disableLink={disableLink}
                                    >
                                        {parse(productTitle)}
                                    </ProductLink>
                                </Text>
                            </Flex>
                            {middleSection || null}
                            {isLimitedQuantityBadge ? <LimitedQuantityLabel /> : null}
                            {isOnlyXLeftBadge && availableToSell ? (
                                <OnlyXLeftLabel quantityLeft={availableToSell} />
                            ) : null}
                            {isSaleFinal && (
                                <Flex gap={1} direction="column">
                                    <Text style="bold" color="slt-red-error">
                                        <FormattedMessage
                                            id="cart-item.final_sale"
                                            defaultMessage="Final Sale:"
                                        />
                                    </Text>
                                    <Text color="slt-red-error">
                                        <FormattedMessage
                                            id="cart-item.non_returnable"
                                            defaultMessage="Non-returnable"
                                        />
                                    </Text>
                                </Flex>
                            )}
                        </Flex>

                        <ConditionalRender visibility={{ base: false, 'lg-sm': true }}>
                            <CartDeliveryOptions productItem={productItem} product={product} />
                        </ConditionalRender>
                    </Flex>

                    <Flex
                        data-testid={`cart-item-price-section-${productId}`}
                        gap={4}
                        direction="column"
                        shrink={0}
                        maxWidth={{ 'lg-sm': '160px', lg: '180px' }} // add max width to the price section to avoid expansion on long messages (SLTBE-6455)
                    >
                        <Flex width="100%" justifyContent={{ base: 'start', 'lg-sm': 'end' }}>
                            <CartItemPrice productItem={productItem} />
                        </Flex>

                        <ConditionalRender visibility={!!quantityComponent}>
                            <Flex width="100%" justifyContent={{ base: 'start', 'lg-sm': 'end' }}>
                                {quantityComponent}
                            </Flex>
                        </ConditionalRender>
                    </Flex>
                </Flex>
            </Flex>

            <ConditionalRender visibility={{ base: true, 'lg-sm': false }}>
                <CartDeliveryOptions productItem={productItem} product={product} />
            </ConditionalRender>

            {footerSection}

            <Flex data-testid="cart-item-buttons-section" width="100%" gap={6}>
                <DeleteProductItemButton productItem={productItem} />
                {buttonSection}
            </Flex>
        </Flex>
    );
};

BaseItem.defaultProps = {
    product: undefined,
    isLimitedQuantityBadge: undefined,
    isOnlyXLeftBadge: undefined,
    isSaleFinal: undefined,
    availableToSell: undefined,
    quantitySection: null,
    middleSection: null,
    footerSection: null,
    buttonSection: null,
    isSoftLink: false,
    disableLink: false,
};
